import type { RuleSet } from '../rules';
import type { Building, SimState } from '../sim';

export interface ConstructionStatus {
  progress: number;
  /** Seconds until complete at the current rate, or null when stalled. */
  secondsLeft: number | null;
}

/**
 * Progress of an unfinished building. Mirrors the construction system:
 * self-building sites (cities) always advance; worker-built sites (farms,
 * forestries) only advance while a worker with that build order stands on
 * the site.
 */
export function constructionStatus(
  state: SimState,
  rules: RuleSet,
  b: Building
): ConstructionStatus | null {
  if (b.buildProgress >= 1) return null;
  const def = rules.buildings[b.type];
  if (!def) return null;
  const advancing =
    def.consumesBuilder ||
    Object.values(state.units).some(
      (u) =>
        u.order?.kind === 'build' &&
        u.order.buildingId === b.id &&
        u.owner === b.owner &&
        u.pos.q === b.pos.q &&
        u.pos.r === b.pos.r
    );
  return {
    progress: b.buildProgress,
    secondsLeft: advancing ? (1 - b.buildProgress) * def.buildTime : null,
  };
}

/** Short label over a site: "Farm · 12s", or "Farm · needs worker" when stalled. */
export function constructionLabel(name: string, status: ConstructionStatus): string {
  return `${name} · ${status.secondsLeft === null ? 'needs worker' : formatDuration(status.secondsLeft)}`;
}

/** 7 → "7s", 75 → "1m 15s". */
export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds));
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
}
